import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { useRouter } from "next/router";

/**
 * URL query params carrying the open workspace pane, e.g.
 * `/sessions/abc?pane=diff&paneId=att_123`. Living on the URL makes the
 * pane shareable: a shared session link opens with the pane already
 * showing. `paneId` optionally identifies the content the pane shows
 * (an attachment, artifact part, ...), resolved by the view at render.
 */
export const WORKSPACE_PANE_QUERY_PARAM = "pane";
export const WORKSPACE_PANE_ID_QUERY_PARAM = "paneId";
export const WORKSPACE_PANE_TYPES = ["diff"] as const;
export type WorkspacePaneType = (typeof WORKSPACE_PANE_TYPES)[number];
export interface WorkspacePaneState {
  type: WorkspacePaneType;
  id?: string;
}
function first(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}
export function parseWorkspacePaneType(value: string | string[] | undefined): WorkspacePaneType | null {
  const raw = first(value);
  return WORKSPACE_PANE_TYPES.includes(raw as WorkspacePaneType) ? raw as WorkspacePaneType : null;
}

/**
 * Workspace pane open/closed state, mirrored to the `?pane=` / `?paneId=`
 * query params.
 *
 * Opening the pane pushes a history entry so the browser back button closes
 * it; closing (or switching content while open) replaces to avoid history
 * spam. All navigations are shallow so the pane never refetches the page.
 * Hosts without a Next router (Storybook stories, unit tests) fall back to
 * plain local state.
 */
export function useWorkspacePaneUrlState() {
  const $ = _c(18);
  const router = useRouter();
  const [localPane, setLocalPane] = useState(null);
  let t0;
  if ($[0] !== localPane?.type || $[1] !== router) {
    t0 = router ? parseWorkspacePaneType(router.query[WORKSPACE_PANE_QUERY_PARAM]) : localPane?.type ?? null;
    $[0] = localPane?.type;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const type = t0;
  let t1;
  if ($[3] !== localPane?.id || $[4] !== router || $[5] !== type) {
    t1 = router ? type !== null ? first(router.query[WORKSPACE_PANE_ID_QUERY_PARAM]) ?? null : null : localPane?.id ?? null;
    $[3] = localPane?.id;
    $[4] = router;
    $[5] = type;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const id = t1;
  let t2;
  if ($[7] !== router) {
    t2 = next => {
      if (!router) {
        setLocalPane(next);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const current = parseWorkspacePaneType(params.get(WORKSPACE_PANE_QUERY_PARAM) ?? undefined);
      if (next === null) {
        params.delete(WORKSPACE_PANE_QUERY_PARAM);
        params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
      } else {
        params.set(WORKSPACE_PANE_QUERY_PARAM, next.type);
        if (next.id !== undefined) {
          params.set(WORKSPACE_PANE_ID_QUERY_PARAM, next.id);
        } else {
          params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
        }
      }
      const [path] = router.asPath.split(/[?#]/, 1);
      const qs = params.toString();
      const url = `${path}${qs ? `?${qs}` : ""}${window.location.hash}`;
      if (current === null && next !== null) {
        router.push(url, undefined, {
          shallow: true,
          scroll: false
        });
      } else {
        router.replace(url, undefined, {
          shallow: true,
          scroll: false
        });
      }
    };
    $[7] = router;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const setPane = t2;
  let t3;
  if ($[9] !== localPane || $[10] !== router || $[11] !== setPane) {
    t3 = pane => {
      const currentlyOpen = router ? parseWorkspacePaneType(new URLSearchParams(window.location.search).get(WORKSPACE_PANE_QUERY_PARAM) ?? undefined) !== null : localPane !== null;
      setPane(currentlyOpen ? null : pane);
    };
    $[9] = localPane;
    $[10] = router;
    $[11] = setPane;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const togglePane = t3;
  let t4;
  if ($[13] !== id || $[14] !== setPane || $[15] !== togglePane || $[16] !== type) {
    t4 = {
      type,
      id,
      setPane,
      togglePane
    };
    $[13] = id;
    $[14] = setPane;
    $[15] = togglePane;
    $[16] = type;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}